"use client"

import clsx from "clsx"
import useEmblaCarousel from "embla-carousel-react"
import { useEffect, useRef } from "react"
import { ChevronLeftIcon, ChevronRightIcon } from "src/components/icons"
import { Img } from "src/components/unstyled/Img"
import { localizeText } from "src/localization/utils"
import { emblaOptions, useEmbla } from "src/utilities/embla"
import { ProductImageSize, toProductImageCdnUrl } from "src/utilities/productImages"
import { scrollIntoView } from "src/utilities/scroll"

export type ImageGalleryMobileProps = {
  title: string
  images: string[]
}

export const ImageGalleryMobile = ({ title, images }: ImageGalleryMobileProps) => {
  const sectionRef = useRef<HTMLDivElement>(null)
  const { ref, embla, activeIndex, hasPrev, hasNext } = useEmbla()
  const [emblaMiniRef, emblaMiniApi] = useEmblaCarousel({ ...emblaOptions, slidesToScroll: 1 })

  useEffect(() => {
    if (!emblaMiniApi) return

    const miniIndex = emblaOptions.loop ? activeIndex : Math.max(0, activeIndex - 1)

    emblaMiniApi.scrollTo(miniIndex)
  }, [activeIndex, emblaMiniApi])

  useEffect(() => {
    if (activeIndex === undefined) return
    scrollIntoView(sectionRef.current)
  }, [activeIndex])

  const T = localizeText(text)

  return (
    <section ref={sectionRef} aria-label={T.productImages} className="-mt-8 flex flex-col gap-4 pt-4 sm:hidden">
      <div className="embla relative">
        <div ref={ref} className="embla__viewport overflow-hidden">
          <ul className="embla__container flex max-h-full gap-4">
            {images.map((src, index) => {
              const alt = [title, index > 0 ? `(${T.image} ${index + 1})` : ""].filter(Boolean).join(" ")

              return (
                <li
                  key={index}
                  className="embla__slide aspect-4/5 max-h-full min-w-0 shrink-0 grow-0 basis-full overflow-hidden rounded-lg border bg-gray-100 shadow-sm"
                >
                  <Img
                    src={toProductImageCdnUrl({
                      src: src,
                      format: "square",
                      size: ProductImageSize.Small,
                    })}
                    alt={alt}
                    loading={index === 0 ? "eager" : "lazy"}
                    className="size-full rounded-lg object-cover"
                  />
                </li>
              )
            })}
          </ul>
        </div>

        {hasPrev && (
          <button
            type="button"
            onClick={() => embla?.scrollPrev()}
            aria-label={T.prevImage}
            className="embla__prev absolute top-1/2 left-0 -translate-y-1/2"
          >
            <ChevronLeftIcon className="size-12 text-white drop-shadow-sm" />
          </button>
        )}
        {hasNext && (
          <button
            type="button"
            onClick={() => embla?.scrollNext()}
            aria-label={T.nextImage}
            className="embla__next absolute top-1/2 right-0 -translate-y-1/2"
          >
            <ChevronRightIcon className="size-12 text-white drop-shadow-sm" />
          </button>
        )}
      </div>

      <div ref={emblaMiniRef} className="embla__viewport overflow-hidden">
        <div className="embla__container -ml-4 flex max-h-full">
          {images.map((src, index) => {
            const alt = [title, index > 0 ? `(${T.image} ${index + 1})` : ""].filter(Boolean).join(" ")
            const active = index === activeIndex

            return (
              <div key={index} className="embla__slide min-w-0 shrink-0 grow-0 basis-28 pl-4">
                <button
                  type="button"
                  onClick={() => embla?.scrollTo(index)}
                  className={clsx(
                    "relative aspect-square h-24 overflow-hidden rounded-lg bg-gray-100 shadow-sm",
                    active && "border-brand-primary border-2",
                  )}
                >
                  <Img
                    src={toProductImageCdnUrl({
                      src,
                      format: "vertical",
                      size: ProductImageSize.Large,
                    })}
                    alt={alt}
                    className="absolute inset-0 rounded-lg object-cover"
                  />
                </button>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

const de = {
  productImages: "Produktbilder",
  nextImage: "Nächstes Bild",
  prevImage: "Vorheriges Bild",
  image: "Bild",
}

const en: typeof de = {
  productImages: "Product images",
  nextImage: "Next image",
  prevImage: "Previous image",
  image: "Image",
}

const text = { de, en }
